import Head from 'next/head';
import { FC, useState, useEffect, memo, useLayoutEffect } from 'react';
// import { useTranslation } from '@/hooks/use-translation';
import { BreadcrumbList } from 'schema-dts';
import { jsonLdScriptProps } from 'react-schemaorg';
import { useRouter } from 'next/router';
import { MetaData } from './PageMeta.types';

const PageMeta = memo((props: MetaData) => {
  const {
    webUrl,
    page,
    settingTitle,
    pageTitle,
    seo,
    openGraph = {},
    blogSnippet,
    wikiCategorySetUpData,
    currentUrl,
    languages,
    banner,
    bio,
    profilePicture,
    language,
    pathName,
    categoryPage
  } = props ?? {};
  // const t = useTranslation();
  // const { pathname } = useRouter();

  const isSearchPage = pathName?.includes('search');
  const searchTitle = language === 'zh' ? '搜索 | 富衛香港' : 'Search | FWD HK';
  const curPageTitle = isSearchPage ? searchTitle : settingTitle ? settingTitle : pageTitle;
  const [breadcrumbList, setBreadcrumbList] = useState<any[]>([]);

  const currentTitle = isSearchPage
    ? searchTitle
    : settingTitle
    ? settingTitle
    : blogSnippet?.blog_title
    ? blogSnippet?.blog_title
    : pageTitle;

  // meta-title
  const metaTitle = seo?.meta_title ? seo?.meta_title : currentTitle;

  // meta-description
  const metaDescription = seo?.meta_description || '';
  const metaDescriptionEle = metaDescription
    ? metaDescription
    : blogSnippet?.description
    ? blogSnippet?.description
    : wikiCategorySetUpData?.description ?? '';

  // meta-keywords
  const metaKeyWords = seo?.meta_keywords?.toString() ?? '';

  // og-title
  const ogTitle = openGraph?.og_title ? openGraph?.og_title : currentTitle;

  // og-description
  const ogDescription = openGraph?.og_description || '';
  const ogDescriptionEle = ogDescription ? ogDescription : metaDescriptionEle;
  const categoryDescription = banner?.description?.toString() ?? '';
  const authorDescription = bio ?? '';
  const ogDes = ogDescriptionEle
    ? ogDescriptionEle
    : pathName === '/blog/author/[authorName]'
    ? authorDescription
    : pathName === '/blog/[category]' || pathName === '/blog/guide'
    ? categoryDescription
    : '';

  // og-image
  const ogImage = openGraph?.og_image?.url
    ? openGraph?.og_image?.url
    : blogSnippet?.banner_mobile?.url;

  // const categoryImageUrl = banner?.banner_mobile?.url ?? '';

  // const authorImageUrl = profilePicture?.url ?? '';

  const formatLangUrl = (url: string, language: string) => {
    const blogIndex = url.indexOf('/blog/');
    if (blogIndex !== -1) {
      const languageIndex = url.lastIndexOf('/', blogIndex - 1);
      if (languageIndex !== -1) {
        const updatedUrl =
          url.substring(0, languageIndex + 1) + language + url.substring(blogIndex);
        return updatedUrl;
      }
    }
    return url;
  };

  const renderLinkElement = () => {
    switch (pathName) {
      case '/blog/[category]/[article]':
      case '/blog/guide/[article]':
      case '/blog/guide':
        if (languages === 'both_lang')
          return (
            <>
              <link rel="alternate" href={formatLangUrl(currentUrl, 'zh')} hrefLang="zh-hk" />
              <link rel="alternate" href={formatLangUrl(currentUrl, 'en')} hrefLang="en-hk" />
            </>
          );
        break;
      default:
        return (
          <>
            <link rel="alternate" href={formatLangUrl(currentUrl, 'zh')} hrefLang="zh-hk" />
            <link rel="alternate" href={formatLangUrl(currentUrl, 'en')} hrefLang="en-hk" />
          </>
        );
        break;
    }
  };

  useEffect(() => {
    (function (w: any, d: any, s: any, l: any, i: any) {
      w[l] = w[l] || [];
      w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      var f = d.getElementsByTagName(s)[0],
        j = d.createElement(s),
        dl = l != 'dataLayer' ? '&l=' + l : '';
      j.async = true;
      j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
      f.parentNode.insertBefore(j, f);
    })(window, document, 'script', 'dataLayer', 'GTM-N6B3G67');
  }, []);

  useEffect(() => {
    let list = [
      {
        name: language == 'zh' ? '首頁' : 'Home',
        link: language == 'zh' ? 'https://www.fwd.com.hk/' : 'https://www.fwd.com.hk/en/'
      },
      {
        name: language == 'zh' ? '網誌' : 'Blog',
        link: window.location.origin + (language == 'zh' ? '/zh/blog/' : '/en/blog/')
      },
      {
        name: page?.category_reference?.length > 0 ? page?.category_reference[0].title : '',
        link:
          window.location.origin +
          (language == 'zh'
            ? `/zh/blog/${page?.url}`.replace(/\/\//g, '/')
            : `/en/blog/${page?.url}`.replace(/\/\//g, '/'))
      }
    ];
    setBreadcrumbList(list);
  }, []);

  return (
    <Head>
      <title>{currentTitle}</title>
      {seo && <meta name="title" content={metaTitle} key="meta_title" />}
      {seo && <meta name="keywords" content={seo?.meta_keywords} key="meta_keywords" />}
      {seo && <meta name="description" content={metaDescriptionEle} key="meta_description" />}
      {seo && <meta name="keywords" content={metaKeyWords} key="meta_keywords" />}
      {openGraph && <meta property="og:title" content={ogTitle} key={'og_title'} />}
      {openGraph && <meta property="og:description" content={ogDes} key="og_description" />}
      {openGraph && <meta property="og:url" content={currentUrl} key={'og_url'} />}
      {openGraph && <meta property="og:image" content={ogImage} key="og_image" />}
      {renderLinkElement()}
      {isSearchPage && <meta name="robots" content="noindex,nofollow" />}
      {seo?.hasOwnProperty('canonical_url') && (
        <link rel="canonical" href={currentUrl} key="canonical_url" />
      )}
      {webUrl === 'https://blog-dev.apse1.dgt.np-api.fwd.com' && (
        <meta name="robots" content="noindex"></meta>
      )}
      {(pathName === '/blog/[category]/[article]' || pathName === '/blog/guide/[article]') && (
        <meta property="og:type" content="article" />
      )}
      {pathName === '/blog/[category]' && (
        <>
          {breadcrumbList?.length > 0 && categoryPage?.records ? (
            <script
              {...jsonLdScriptProps<BreadcrumbList>({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: breadcrumbList?.map((item: any, index: number) => {
                  return {
                    '@type': 'ListItem',
                    position: index + 1,
                    name: item.name,
                    item: item.link
                  };
                })
              })}
            />
          ) : (
            ''
          )}
        </>
      )}
    </Head>
  );
});
export default PageMeta;
